import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Routes } from '@angular/router';
import { TokenInterceptor } from '@app/core/http.interceptor';
import { CreativeListItemMenuComponent } from '@app/manage-view/creative-list-item-menu/creative-list-item-menu.component';
import { CreativeListItemPreviewComponent } from '@app/manage-view/creative-list-item-preview/creative-list-item-preview.component';
import { CreativeListItemSelectComponent } from '@app/manage-view/creative-list-item-select/creative-list-item-select.component';
import { CreativeListItemTitleComponent } from '@app/manage-view/creative-list-item-title/creative-list-item-title.component';
import { CreativeListItemWeightComponent } from '@app/manage-view/creative-list-item-weight/creative-list-item-weight.component';
import { HeavyVideoIndicatorComponent } from '@app/manage-view/heavy-video-indicator/heavy-video-indicator.component';
import { SizeAddCustomInputComponent } from '@app/manage-view/size-add-dialog/size-add-custom-input/size-add-custom-input.component';
import { SizeAddOverviewComponent } from '@app/manage-view/size-add-dialog/size-add-overview/size-add-overview.component';
import { SizeAddPreviewComponent } from '@app/manage-view/size-add-dialog/size-add-preview/size-add-preview.component';
import { TestIdDirective } from '@app/shared/directives';
import { CommentsModule } from '@bannerflow/comments';
import { CreativesetShowcaseModule } from '../../shared/creativeset-showcase/creativeset-showcase.module';
import { ManageViewComponent } from '../index';
import { AddToCampaignDropdownComponent } from './add-to-campaign-dropdown/add-to-campaign-dropdown.component';
import { AnimationControlModule } from '@app/shared/animation-control/animation-control.module';
import { CampaignsDropdownComponent } from './campaigns-dropdown/campaigns-dropdown.component';
import { MVContextMenuComponent } from './context-menu';
import { PsdImportPreviewComponent } from './creative-converter/psd/psd-import-preview/psd-import-preview.component';
import { PsdListComponent } from './creative-converter/psd/psd-list/psd-list.component';
import {
    DeleteActiveSizeDialogComponent,
    DeleteCreativeDialogComponent,
    DeleteSizeDialogComponent
} from './delete-creative-dialog';
import { DuplicateCreativeDialogComponent } from './duplicate-creative-dialog/duplicate-creative-dialog.component';
import { VersionListComponent } from './duplicate-creative-dialog/version-list/version-list.component';
import { DuplicateCreativesModule } from './duplicate-creatives/duplicate-creatives.module';
import { ExportCreativeModule } from './export-creative';
import {
    FilterListComponent,
    FilterListSizesComponent,
    FilterListStatusComponent
} from './filter-list';
import { FullScreenDialogComponent } from './full-screen-dialog/full-screen-dialog.component';
import { ManageMenuComponent } from './manage-menu/manage-menu.component';
import { StudioTopbarComponent } from '@app/shared/studio-topbar/studio-topbar.component';
import { CampaignActionsComponent } from './campaign-actions/campaign-actions.component';
import { PublishChangesDialogComponent } from './publish-changes-dialog';
import { RichTextInputComponent, RichTextMenuComponent } from './rich-text-input';
import { RichTextPreviewComponent } from './rich-text-preview/rich-text-preview.component';
import { ShowcaseDialogComponent } from './showcase/showcase-dialog.component';
import { SizeAddDialogComponent } from './size-add-dialog/size-add-dialog.component';
import { SizeAddTabsComponent } from './size-add-dialog/size-add-tabs/size-add-tabs.component';
import { SizeListComponent } from './size-add-dialog/size-list/size-list.component';
import { SizeThumbnailComponent } from './size-add-dialog/size-list/size-thumbnail/size-thumbnail.component';
import { SizeListHorizontalComponent } from './size-list-horizontal/size-list-horizontal.component';
import { SizeSelectorDialogComponent } from './size-selector-dialog/size-selector-dialog.component';
import { SizeSelectorComponent } from './size-selector/size-selector.component';
import { StatusDropdownComponent } from './status-dropdown/status-dropdown.component';
import { TargetUrlDialogComponent } from './target-url-dialog/target-url-dialog.component';
import {
    MvFeedPropertiesComponent,
    MvTextPropertiesComponent,
    TranslationPanelComponent
} from './translation-panel';
import { MvWidgetPropertiesComponent } from './translation-panel/mv-widget-properties/mv-widget-properties.component';
import { UrlDialogComponent } from './url-dialog';
import { CreativeListModule } from '@app/shared/creative-list/creative-list.module';
import { CreativeListItemMetaComponent } from '@app/shared/creative-list/creative-list-item-meta/creative-list-item-meta.component';
import { SharedMVTPModule } from '@app/shared/shared-mv-tp.module';

const routes: Routes = [
    {
        path: '',
        component: ManageViewComponent
    }
];

@NgModule({
    imports: [
        CommonModule,
        ScrollingModule,
        FormsModule,
        RouterModule.forChild(routes),
        SharedMVTPModule,
        CommentsModule,
        CreativeListModule,

        // Stores
        DuplicateCreativesModule,
        CreativesetShowcaseModule,
        ExportCreativeModule,
        AnimationControlModule,

        // standalone things
        StudioTopbarComponent,
        HeavyVideoIndicatorComponent,
        CreativeListItemPreviewComponent,
        CreativeListItemWeightComponent,
        CreativeListItemTitleComponent,
        CreativeListItemMenuComponent,
        CreativeListItemMetaComponent,
        StatusDropdownComponent,
        CreativeListItemSelectComponent,
        SizeAddPreviewComponent,
        SizeAddCustomInputComponent,
        SizeAddOverviewComponent,
        SizeAddTabsComponent,
        PsdImportPreviewComponent,
        PsdListComponent,
        TestIdDirective
    ],
    exports: [RouterModule],
    declarations: [
        ManageViewComponent,
        AddToCampaignDropdownComponent,
        CampaignsDropdownComponent,
        MVContextMenuComponent,
        DeleteCreativeDialogComponent,
        DeleteSizeDialogComponent,
        DeleteActiveSizeDialogComponent,
        SizeAddDialogComponent,
        SizeListComponent,
        SizeThumbnailComponent,
        VersionListComponent,
        FilterListComponent,
        FilterListSizesComponent,
        FilterListStatusComponent,
        ManageMenuComponent,
        PublishChangesDialogComponent,
        UrlDialogComponent,
        ShowcaseDialogComponent,
        RichTextInputComponent,
        RichTextPreviewComponent,
        RichTextMenuComponent,
        DuplicateCreativeDialogComponent,
        MvFeedPropertiesComponent,
        MvTextPropertiesComponent,
        MvWidgetPropertiesComponent,
        TranslationPanelComponent,
        CampaignActionsComponent,
        SizeSelectorComponent,
        SizeSelectorDialogComponent,
        SizeListHorizontalComponent,
        TargetUrlDialogComponent,
        FullScreenDialogComponent
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        }
    ]
})
export class ManageViewModule {}
